import { defineStore } from 'pinia'
import { CACHE_TTL_MS } from '~/constants/timing'

interface CampaignStatusesState {
    statuses: CampaignStatus[]
    isLoading: boolean
    lastFetched: Date | null
}

export const useCampaignStatusesStore = defineStore('campaignStatuses', {
    state: (): CampaignStatusesState => ({
        statuses: [],
        isLoading: false,
        lastFetched: null,
    }),

    getters: {
        failedStatuses: (state): CampaignStatus[] =>
            state.statuses.filter((s: CampaignStatus & { type?: string }) => s.type === 'failed'),

        statusOptions: (state): { title: string; value: string }[] => {
            const { $i18n } = useNuxtApp()
            return state.statuses.map(s => ({
                title: $i18n.t(s.code),
                value: s.code,
            }))
        },

        isStale: (state): boolean => {
            if (!state.lastFetched) return true
            return Date.now() - state.lastFetched.getTime() > CACHE_TTL_MS
        },
    },

    actions: {
        async fetchStatuses(force = false) {
            if (this.isLoading) return
            if (!force && !this.isStale && this.statuses.length > 0) return

            this.isLoading = true
            try {
                const { $customFetch } = useNuxtApp()
                const { data } = await $customFetch<ApiResponseList<CampaignStatus>>('/api/campaigns/statuses', {
                    method: 'GET',
                })
                this.statuses = data
                this.lastFetched = new Date()
            } catch (e) {
                console.error('Failed to fetch campaign statuses:', e)
            } finally {
                this.isLoading = false
            }
        },

        init() {
            if (import.meta.client) {
                this.fetchStatuses()
            }
        },
    },
})
